"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/shared/PlaidUploadedDocumentChip";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import type { UploadedAttachmentFile } from "@/src/utils/attachmentUpload/uploadAttachmentFiles";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidUploadItem extends UploadedAttachmentFile {
  id: string;
  status?: "uploading" | "done" | "error";
  /** 本地压缩预览 blob URL（图片）；移除 / 卸载时由 hook 释放（EDG-561 SC-06 / SC-08） */
  previewUrl?: string;
  /** 原始文件引用：失败后「Try again」只重传失败项，不要求用户重选（EDG-561 SC-07） */
  file?: File;
}

export interface PlaidUploadedDocumentChipProps {
  file: PlaidUploadItem;
  onRemove?: () => void;
  /** 仅图片缩略图可点放大；PDF / CSV 类型块不可点（PRD S3.2） */
  onPreview?: (file: PlaidUploadItem) => void;
  /**
   * `overlap`：Figma 的 hug 宽 chip（`109896:82244`），用于 wrap 布局；
   * `default`：V3.0.6 现状（纵向列表的描边卡片），保持不变。
   */
  variant?: "default" | "overlap";
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1);
  return `${size} ${units[i]}`;
}

function getFileTypeInfo(filename: string, mediaType?: string) {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf" || mediaType === "application/pdf") {
    return {
      kind: "pdf" as const,
      label: "PDF",
      iconSrc: "/assets/images/plaid/manual-upload/guide-pdf.svg",
      bgClass: "bg-background_accent2_overlay10 text-content_accent2_primary",
    };
  }
  if (
    ["png", "jpg", "jpeg", "webp"].includes(ext) ||
    mediaType?.startsWith("image/")
  ) {
    return {
      kind: "image" as const,
      label: ext.toUpperCase() || "JPG",
      iconSrc: "/assets/images/plaid/manual-upload/guide-landscape.svg",
      bgClass: "bg-background_accent5_overlay20 text-content_accent5_primary",
    };
  }
  if (
    ["csv", "xlsx", "xls"].includes(ext) ||
    mediaType?.includes("csv") ||
    mediaType?.includes("excel") ||
    mediaType?.includes("spreadsheet")
  ) {
    return {
      kind: "sheet" as const,
      label: ext.toUpperCase() || "CSV",
      iconSrc: "/assets/images/plaid/manual-upload/guide-csv.svg",
      bgClass: "bg-background_auralv2_overlay10 text-content_auralv2_primary",
    };
  }
  return {
    kind: "doc" as const,
    label: ext.toUpperCase() || "DOC",
    iconSrc: "/assets/images/plaid/manual-upload/guide-csv.svg",
    bgClass: "bg-background_brand_overlay10 text-content_brand_primary",
  };
}

export const PlaidUploadedDocumentChip: React.FC<
  PlaidUploadedDocumentChipProps
> = ({ file, onRemove, onPreview, variant = "default", className = "" }) => {
  const typeInfo = getFileTypeInfo(file.filename, file.mediaType);
  const isUploading = file.status === "uploading";
  const isError = file.status === "error";
  const isOverlap = variant === "overlap";
  const thumbSrc =
    typeInfo.kind === "image"
      ? (file.previewUrl ?? (file.url || undefined))
      : undefined;

  // 图片且可放大时，整张 chip 都是热区（不只缩略图）；右侧移除钮 stopPropagation，不触发放大
  const previewable = Boolean(thumbSrc && onPreview);
  const openPreview = () => onPreview?.(file);

  const rootClassName = `${
    isOverlap
      ? `flex items-center gap-1 p-1.5 rounded-2 bg-background_white_overlay5 ${className}`
      : `bg-background_solid_primary border border-border_main rounded-2 p-2 flex items-center justify-between gap-2.5 transition-all shadow-sm ${className}`
  }${previewable ? " cursor-pointer" : ""}`;

  const body = (
    <>
      {/* Left thumbnail & copy */}
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {thumbSrc ? (
          // 图片缩略图（所有 entry 生效，AC-3.9）：32×32、圆角 6、顶部裁切（PRD）；点击由整张 chip 承担
          <div
            data-testid="plaid-uploaded-doc-thumb"
            className="size-8 shrink-0 overflow-hidden rounded-[6px]"
          >
            <img
              src={thumbSrc}
              alt=""
              className="size-8 rounded-[6px] object-cover object-top"
            />
          </div>
        ) : (
          <div
            className={`size-8 rounded-[6px] flex items-center justify-center shrink-0 ${typeInfo.bgClass}`}
          >
            <img
              src={typeInfo.iconSrc}
              alt=""
              aria-hidden="true"
              className="h-[18px] w-[18px]"
            />
          </div>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <p className="text-xs font-medium text-content_primary truncate leading-4">
            {file.filename}
          </p>
          <p className="text-[10px] text-content_tertiary leading-3 mt-0.5">
            {/* `~...~` 为 i18n 扫描标记（review #7）：scan 会改写为 I18n.t 并生成 translate 模块 */}
            {isUploading
              ? I18n.t("Uploading...")
              : isError
                ? I18n.t("Upload failed")
                : `${typeInfo.label} · ${formatFileSize(file.size)}`}
          </p>
        </div>
      </div>

      {/* Right status or remove button */}
      <div className="shrink-0 flex items-center justify-center">
        {isUploading ? (
          <Icon
            name="Spinner"
            size={16}
            className="animate-spin text-content_brand_primary"
          />
        ) : onRemove ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            data-openreplay-label="plaidconnectflow_upload_remove"
            className="size-6 flex items-center justify-center text-content_tertiary hover:text-content_primary active:scale-95 transition-all cursor-pointer rounded-full hover:bg-black/5 dark:hover:bg-white/5"
            aria-label={I18n.t("Remove %{var0}", {
              var0: file.filename,
            })}
          >
            <Icon name="close" size={14} />
          </button>
        ) : null}
      </div>
    </>
  );

  // label 必须是字面量写在元素上（埋点扫描器按字面量识别）；不可放大的 chip（PDF / CSV / 无来源）不挂
  return previewable ? (
    <div
      className={rootClassName}
      data-testid="plaid-uploaded-doc-chip"
      role="button"
      tabIndex={0}
      onClick={openPreview}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPreview();
        }
      }}
      data-openreplay-label="plaidconnectflow_upload_preview"
      aria-label={I18n.t("Preview %{var0}", { var0: file.filename })}
    >
      {body}
    </div>
  ) : (
    <div className={rootClassName} data-testid="plaid-uploaded-doc-chip">
      {body}
    </div>
  );
};
